import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import PageTitle from "@/Shared/Layout/PageTitle";
import { WebhooksDeliveriesFilterBar } from "./Bars/WebhooksDeliveriesFilterBar";
import { WebhooksDeliveriesTable } from "./Tables/WebhooksDeliveriesTable";
import { useWebhooksDeliveriesScreen } from "./Hooks/useWebhooksDeliveriesScreen";

/**
 * Every event FreightSimple pushed to the customer's webhooks in the last few weeks, with a way
 * to see what went over the wire and to re-send a failed one. Before this, a lost delivery was
 * invisible to the customer and unrecoverable without ops. Built for a company with thousands
 * of deliveries: the list is filtered, sorted and paged on the server.
 */
export function WebhooksDeliveriesScreen() {
  const {
    webhooks,
    hasRecentDeliveries,
    webhooksLoadFailed,
    page,
    filters,
    retryingDeliveryId,
    onQueryChange,
    onView,
    onRetry,
    dialogs,
  } = useWebhooksDeliveriesScreen();

  return (
    <>
      <PageTitle>Webhook Deliveries</PageTitle>
      {/* Outside the layout: the loading gate and the empty state each replace the content, and a
          dialog rendered inside either would be unmounted with it. */}
      {dialogs}
      <FullWidthLayout
        header="Webhook Deliveries"
        microcopy={
          page
            ? `Everything we sent to your webhooks in the last ${page.retentionDays} days. A failed delivery can be re-sent up to ${page.maxCustomerRetries} times.`
            : "Everything we sent to your webhooks, and whether it arrived"
        }
        loading={webhooks === undefined && !webhooksLoadFailed}
        // A company that deleted its webhooks keeps its history for the retention window, so the
        // empty state is for a company with nothing at all.
        showEmptyState={
          webhooks !== undefined &&
          webhooks.length === 0 &&
          !hasRecentDeliveries
        }
        emptyState={
          <Stack gap={2} align="center">
            <Heading level={1}>No webhooks are set up for your account</Heading>
            <T color="secondary">
              Webhooks let your systems hear about shipment events as they
              happen. Contact FreightSimple to set one up.
            </T>
          </Stack>
        }
        content={
          <Stack gap={4}>
            <WebhooksDeliveriesFilterBar
              webhooks={webhooks ?? []}
              filter={filters.filter}
              setWebhookId={filters.setWebhookId}
              setEventType={filters.setEventType}
              setState={filters.setState}
              setDateRange={filters.setDateRange}
            />
            <WebhooksDeliveriesTable
              deliveries={page?.deliveries ?? []}
              totalCount={page?.totalCount ?? 0}
              maxCustomerRetries={page?.maxCustomerRetries ?? 0}
              webhooks={webhooks ?? []}
              resetPageKey={filters.resetPageKey}
              onQueryChange={onQueryChange}
              retryingDeliveryId={retryingDeliveryId}
              onView={onView}
              onRetry={onRetry}
            />
          </Stack>
        }
      />
    </>
  );
}
